import { PermissionError } from '@api/generated/public/publicApi'
import { isAxiosError } from 'axios'

/**
 * A failed API call, normalized across the two HTTP clients this app uses.
 *
 * `agentApi` (generated by swagger-typescript-api, see `api/generated/internal/internalApi.ts`) is
 * built on `fetch` and throws the augmented `Response` itself: the parsed body lands on `.error`,
 * and there is no `.response` wrapper. The older hand-written clients are Axios based and put the
 * body on `.response.data`. Code that only knew the Axios shape silently fell through to a generic
 * "unexpected error" for every generated-client failure (WF-2745), so read both shapes through here.
 */
export interface ApiErrorResponse {
  status: number
  statusText: string
  /** Parsed response body, when the server sent one this client could decode. */
  body: unknown
}

/**
 * Normalizes an error thrown by either HTTP client into {@link ApiErrorResponse}.
 *
 * Returns `undefined` when the error carries no HTTP response at all — a network failure, an aborted
 * request, or a plain `Error` thrown before the call went out. Callers should treat that as
 * "the request never got a reply" rather than as a status-bearing failure.
 */
export function getApiErrorResponse(error: unknown): ApiErrorResponse | undefined {
  if (isAxiosError(error)) {
    if (!error.response) return undefined
    return {
      status: error.response.status,
      statusText: error.response.statusText,
      body: error.response.data,
    }
  }

  // Generated fetch client: `throw data` where data is the Response augmented with `.data`/`.error`.
  if (isRecord(error) && typeof error.status === 'number' && typeof error.ok === 'boolean') {
    return {
      status: error.status,
      statusText: typeof error.statusText === 'string' ? error.statusText : '',
      // `.error` holds the decoded body for a failed response, but it holds the decoding failure
      // itself when the body was not JSON (an Express `res.send('Forbidden')`, say), so drop Errors.
      body: error.error instanceof Error ? undefined : error.error,
    }
  }

  return undefined
}

/**
 * Whether the request went out but never got a reply — the backend is unreachable rather than
 * rejecting us. Axios reports this as an error with no `response`; `fetch` rejects with a `TypeError`.
 * Only meaningful for an error {@link getApiErrorResponse} returned `undefined` for.
 */
export function isNetworkError(error: unknown): boolean {
  if (isAxiosError(error)) return !error.response
  return error instanceof TypeError
}

/**
 * The `PERMISSION_DENIED` body the prediction gate returns when the caller lacks access to a model
 * or embedding provider used by the Teammate, or `undefined` when the failure was some other 403.
 */
export function getPermissionDeniedBody(body: unknown): PermissionError | undefined {
  if (!isRecord(body) || body.code !== 'PERMISSION_DENIED') return undefined
  return body as unknown as PermissionError
}

/** The body of a 4xx the backend explicitly marked as safe to show the user verbatim. */
export function getUserFacingMessage(body: unknown): string | undefined {
  if (!isRecord(body)) return undefined
  if (!body.userFacing) return undefined
  return typeof body.message === 'string' && body.message.length > 0 ? body.message : undefined
}

/** A loggable one-liner for an API failure, used for console diagnostics. */
export function describeApiErrorResponse(response: ApiErrorResponse): string {
  if (response.body === undefined || response.body === null) {
    return `[${response.status}]: ${response.statusText}`
  }
  return `[${response.status}]: ${typeof response.body === 'string' ? response.body : JSON.stringify(response.body)}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
